import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import LazyLoad from 'react-lazy-load';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';

import { HORIZONTAL_OFFSET_RESP_IMG } from '../../../../constants/imageConstants';
import { charLimitedString } from '../../../helpers/charLimitedString';
import { SIZES, SRC_SET_WIDTHS } from './constants';

import { type Image_item$key } from './__generated__/Image_item.graphql';
import { type Image_sortedPhoto$key } from './__generated__/Image_sortedPhoto.graphql';

import styles from './Image.scss';

const itemFragment = graphql`
    fragment Image_item on Item {
        description
    }
`;

const sortedPhotoFragment = graphql`
    fragment Image_sortedPhoto on ItemPhoto {
        masterOrZoomPath
        alt
    }
`;

export const Image: FC<{
    item: Image_item$key;
    sortedPhoto: Image_sortedPhoto$key;
    eagerLoad?: boolean;
    size?: 'xxSmall' | 'xSmall' | 'small' | 'medium' | 'large';
}> = ({ item: itemRef, sortedPhoto: sortedPhotoRef, eagerLoad = false, size = 'large' }) => {
    const item = useFragment(itemFragment, itemRef);
    const sortedPhoto = useFragment(sortedPhotoFragment, sortedPhotoRef);

    let child: ReactNode = null;

    const { masterOrZoomPath, alt } = sortedPhoto;
    const shortDescription = item.description ? charLimitedString(item.description, 250) : '';
    const srcSet = getSrcsetString(masterOrZoomPath || '', SRC_SET_WIDTHS);

    if (masterOrZoomPath && alt && srcSet) {
        child = (
            <figure className={styles.figureWrapper}>
                <picture>
                    <img
                        src={masterOrZoomPath}
                        alt={alt}
                        className={`${styles.mainPhoto} ${styles[size]}`}
                        sizes={SIZES}
                        srcSet={srcSet}
                        {...(eagerLoad ? { fetchpriority: 'high' } : {})}
                    />
                </picture>
                <figcaption className={styles.figureDescription}>{shortDescription}</figcaption>
            </figure>
        );

        if (!eagerLoad) {
            child = (
                <>
                    <LazyLoad
                        offset={HORIZONTAL_OFFSET_RESP_IMG}
                        className={styles.lazyLoadWrapper}
                    >
                        {child}
                    </LazyLoad>
                    <noscript
                        dangerouslySetInnerHTML={{
                            __html: `<img src="${masterOrZoomPath}" alt="${alt}" />`,
                        }}
                    />
                </>
            );
        }
    }

    return child;
};
